/**
 * useMyActiveTasks - Tarefas ativas do usuário logado (atalho do cabeçalho)
 *
 * Compõe sobre a RPC existente `get_tasks_paginated` (mesma usada pela listagem),
 * filtrando: responsável = usuário logado, status diferente de concluída/cancelada,
 * com `p_task_type` explícito (não depende da rota atual).
 *
 * A query key começa com 'tasks-paginated' para que as invalidações já existentes
 * (pausar, retomar, iniciar, mudar status) atualizem o painel automaticamente.
 *
 * @version 1.0.0
 * RELEASE: 2026-10-04 v1.0.0 - Criação inicial (botão flutuante "Minhas tarefas")
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { TaskListItem } from '@/hooks/useTasksPaginated';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

const logger = createLogger('useMyActiveTasks');

/**
 * @param taskType - Módulo (normal | retention | legal)
 * @param enabled - Habilita a consulta
 */
export function useMyActiveTasks(taskType: TaskModuleType, enabled = true) {
  const { currentTenant, user } = useAuth();

  return useQuery({
    queryKey: ['tasks-paginated', 'my-active', currentTenant?.id, user?.id, taskType],
    enabled: enabled && !!currentTenant?.id && !!user?.id,
    staleTime: 15_000,
    // Atualiza periodicamente apenas com a aba do navegador visível
    refetchInterval: () => (typeof document !== 'undefined' && document.visibilityState === 'visible' ? 60_000 : false),
    queryFn: async (): Promise<TaskListItem[]> => {
      const { data, error } = await supabase.rpc('get_tasks_paginated', {
        p_page: 1,
        p_per_page: 50,
        p_assigned_to_filter: user!.id,
        p_status_not_in: ['completed', 'cancelled'],
        p_hide_completed: true,
        p_sort_by: 'created_at',
        p_sort_dir: 'desc',
        p_task_type: taskType,
        p_tenant_hint: currentTenant?.id || null,
      } as any);
      if (error) {
        logger.error('Erro ao buscar minhas tarefas ativas', { error: error.message });
        throw error;
      }
      return (data || []) as TaskListItem[];
    },
  });
}
